import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { ReaderTag } from '@/api/client';
import { computeCatalogTagsInlineFit, type CatalogTagsInlineFitResult } from './computeCatalogTagsInlineFit';

const EMPTY_FIT: CatalogTagsInlineFitResult = {
  visibleCount: 0,
  row1Count: 0,
  showMore: false,
  hiddenCount: 0,
  lastTagMaxWidth: undefined,
};

function initialFitForTags(tags: ReaderTag[]): CatalogTagsInlineFitResult {
  if (tags.length === 0) return EMPTY_FIT;
  return {
    visibleCount: tags.length,
    row1Count: tags.length,
    showMore: false,
    hiddenCount: 0,
    lastTagMaxWidth: undefined,
  };
}

const MORE_WIDTH_CACHE = new Map<number, number>();

export function useCatalogTagsInlineFit(
  containerRef: React.RefObject<HTMLElement | null>,
  tags: ReaderTag[]
) {
  const measurerRef = useRef<HTMLDivElement | null>(null);
  const moreWidthsRef = useRef<Map<number, number>>(new Map());
  const [fit, setFit] = useState<CatalogTagsInlineFitResult>(() => initialFitForTags(tags));

  const measure = useCallback(() => {
    const container = containerRef.current;
    const measurer = measurerRef.current;
    if (!container || !measurer || tags.length === 0) {
      setFit(EMPTY_FIT);
      return;
    }

    const clientWidth = container.clientWidth;
    const pillEls = measurer.querySelectorAll<HTMLElement>('[data-catalog-tag-pill-measure]');
    const naturalWidths: number[] = [];
    pillEls.forEach((el) => {
      naturalWidths.push(el.offsetWidth);
    });

    if (naturalWidths.length !== tags.length) {
      requestAnimationFrame(measure);
      return;
    }

    const moreBadgeWidth = (hiddenCount: number): number => {
      const cached = moreWidthsRef.current.get(hiddenCount);
      if (cached != null) return cached;
      const el = measurer.querySelector<HTMLElement>(
        `[data-catalog-tag-more-measure="${hiddenCount}"]`
      );
      const w = el?.offsetWidth ?? MORE_WIDTH_CACHE.get(hiddenCount) ?? 48;
      moreWidthsRef.current.set(hiddenCount, w);
      MORE_WIDTH_CACHE.set(hiddenCount, w);
      return w;
    };

    const next = computeCatalogTagsInlineFit(clientWidth, naturalWidths, moreBadgeWidth);
    setFit(next);
  }, [containerRef, tags]);

  useLayoutEffect(() => {
    setFit(initialFitForTags(tags));
    measure();
  }, [measure, tags]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measurer = measurerRef.current;
    if (!container && !measurer) return;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(measure);
    });
    if (container) ro.observe(container);
    if (measurer) ro.observe(measurer);
    return () => ro.disconnect();
  }, [containerRef, measure]);

  return { fit, measurerRef, measure };
}
